import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/Layout';
import { Progress } from '../../components/ui/progress';
import {
  Users, PawPrint, CheckCircle, Play, TrendingUp, Award, DollarSign, Package
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  sage: '#7C9A7E',
  navy: '#2D3B47',
  blue: '#3B82F6',
  purple: '#8B5CF6',
  amber: '#F59E0B',
};

function formatMonth(monthStr) {
  if (!monthStr) return '';
  const [year, month] = monthStr.split('-');
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[parseInt(month, 10) - 1]} ${year.slice(2)}`;
}

function StatCard({ icon: Icon, iconBg, iconColor, value, label, sub }) {
  return (
    <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-4 mb-3">
        <div className={`w-12 h-12 ${iconBg} rounded-xl flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${iconColor}`} />
        </div>
        <div>
          <div className="font-heading text-3xl font-bold text-gray-800">{value}</div>
          <div className="text-sm text-gray-500 font-body">{label}</div>
        </div>
      </div>
      {sub && <div className="text-sm text-gray-400 font-body">{sub}</div>}
    </div>
  );
}

function ChartCard({ title, children, testId }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm" data-testid={testId}>
      <div className="p-6 border-b border-gray-100">
        <h2 className="font-heading text-lg font-bold text-gray-800">{title}</h2>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-heading font-bold text-gray-700 mb-1">{formatMonth(label)}</p>
      {payload.map((entry, i) => (
        <p key={i} className="font-body" style={{ color: entry.color }}>
          {entry.name}: {entry.name === 'Revenue' ? `$${entry.value.toLocaleString()}` : entry.value}
        </p>
      ))}
    </div>
  );
};

export default function AdminAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [trends, setTrends] = useState([]);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, trendsRes, seriesRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`),
        axios.get(`${API}/admin/analytics/trends`),
        axios.get(`${API}/series`)
      ]);
      setAnalytics(analyticsRes.data);
      setTrends(trendsRes.data);
      setSeries(seriesRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-edupaws-sage border-t-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  const chartData = trends.map(t => ({
    ...t,
    monthLabel: formatMonth(t.month),
  }));

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-gray-800 mb-2" data-testid="analytics-title">
          Analytics Overview
        </h1>
        <p className="text-gray-500 font-body">
          Track engagement and progress across your program.
        </p>
      </div>

      {/* Main Stats */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        <StatCard
          icon={Users} iconBg="bg-blue-100" iconColor="text-blue-600"
          value={analytics?.owners_count || 0} label="Total Owners"
          sub="Registered owners"
        />
        <StatCard
          icon={PawPrint} iconBg="bg-edupaws-sage/20" iconColor="text-edupaws-sage"
          value={analytics?.dogs_count || 0} label="Dogs Enrolled"
          sub="Active in program"
        />
        <StatCard
          icon={CheckCircle} iconBg="bg-edupaws-sage/20" iconColor="text-edupaws-sage"
          value={`${analytics?.completion_rate || 0}%`} label="Completion Rate"
        />
        <StatCard
          icon={DollarSign} iconBg="bg-amber-100" iconColor="text-amber-600"
          value={`$${(analytics?.total_revenue || 0).toLocaleString()}`} label="Total Revenue"
          sub="Lifetime earnings"
        />
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6 mb-10">
        {/* Owner Growth */}
        <ChartCard title="Owner Growth" testId="chart-owner-growth">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="gradSignups" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.sage} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={COLORS.sage} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tickFormatter={formatMonth} tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone" dataKey="signups" name="Signups"
                stroke={COLORS.sage} fill="url(#gradSignups)" strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Revenue Trend */}
        <ChartCard title="Revenue Trend" testId="chart-revenue-trend">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tickFormatter={formatMonth} tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v) => `$${v}`} tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="revenue" name="Revenue" fill={COLORS.navy} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Second Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6 mb-10">
        {/* Video Completions */}
        <ChartCard title="Video Completions" testId="chart-video-completions">
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tickFormatter={formatMonth} tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone" dataKey="completions" name="Completions"
                stroke={COLORS.purple} strokeWidth={2} dot={{ r: 4, fill: COLORS.purple }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Orders Over Time */}
        <ChartCard title="Orders Over Time" testId="chart-orders">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tickFormatter={formatMonth} tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="orders" name="Orders" fill={COLORS.blue} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Series Performance */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-10" data-testid="series-overview">
        <div className="p-6 border-b border-gray-100">
          <h2 className="font-heading text-xl font-bold text-gray-800">
            Series Overview
          </h2>
        </div>
        <div className="p-6">
          <div className="space-y-6">
            {series.map((s, idx) => (
              <div key={s.id} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center font-heading font-bold text-gray-600 text-sm">
                      {s.order_index}
                    </span>
                    <span className="font-body font-medium text-gray-800">{s.title}</span>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-body font-medium
                    ${s.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {s.published ? 'Published' : 'Draft'}
                  </span>
                </div>
                <div className="ml-11">
                  <Progress value={Math.min((idx + 1) * 15, 100)} className="h-2" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-gradient-to-br from-edupaws-navy to-edupaws-navy/80 rounded-xl p-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <Award className="w-8 h-8" />
            <h3 className="font-heading text-xl font-bold">Program Highlights</h3>
          </div>
          <ul className="space-y-2 font-body">
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              3 comprehensive levels
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Expert-led video lessons
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Progress tracking per dog
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Sequential unlock system
            </li>
          </ul>
        </div>

        <div className="bg-gradient-to-br from-edupaws-sage to-edupaws-sage/80 rounded-xl p-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <TrendingUp className="w-8 h-8" />
            <h3 className="font-heading text-xl font-bold">Quick Actions</h3>
          </div>
          <div className="space-y-3 font-body">
            <p className="text-white/90">
              Keep your content fresh and engaging:
            </p>
            <ul className="space-y-2">
              <li className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5" /> Review video engagement regularly</li>
              <li className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5" /> Update resource links as needed</li>
              <li className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5" /> Monitor completion drop-off points</li>
              <li className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5" /> Gather feedback from top performers</li>
            </ul>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
